import React, { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useOutreachLists } from '../hooks/useOutreachLists';
import { contactsMessages as messages } from './contactsMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ImportCheckStepProps {
  accountId: number;
  ready: number;
  noEmail: number;
  consent: boolean;
  onConsent: (value: boolean) => void;
  listUuid: string | null;
  onList: (value: string | null) => void;
  tag: string;
  onTag: (value: string) => void;
}

/** Step 3 of the import (design outreach q3): the counts, the consent (spec §11.2), the list and a tag. */
const ImportCheckStepComponent: React.FC<ImportCheckStepProps> = ({
  accountId,
  ready,
  noEmail,
  consent,
  onConsent,
  listUuid,
  onList,
  tag,
  onTag,
}) => {
  const intl = useIntl();
  const consentId = useId();
  const { lists } = useOutreachLists(accountId);
  const options = useMemo(
    () =>
      lists.map((list) => (
        <option key={list.listUuid} value={list.listUuid}>
          {list.name}
        </option>
      )),
    [lists]
  );

  return (
    <>
      <div className={shared.alert}>
        <span>{withBrandWord(intl.formatMessage(messages.checkBody, { ready, noEmail }))}</span>
      </div>
      <div className="form-check">
        <input
          id={consentId}
          type="checkbox"
          className="form-check-input"
          checked={consent}
          onChange={(event) => onConsent(event.target.checked)}
        />
        <label className="form-check-label" htmlFor={consentId}>
          <b>{intl.formatMessage(messages.consent)}</b>
          <span className={`${shared.small} d-block`}>{withBrandWord(intl.formatMessage(messages.consentBody))}</span>
        </label>
      </div>
      <div className={shared.row}>
        <label className={shared.label} htmlFor="outreach-import-list">
          {intl.formatMessage(messages.addToList)}
        </label>
        <select
          id="outreach-import-list"
          className="form-select form-select-sm w-auto"
          value={listUuid ?? ''}
          onChange={(event) => onList(event.target.value === '' ? null : event.target.value)}
        >
          <option value="">{intl.formatMessage(messages.noList)}</option>
          {options}
        </select>
        <input
          className={`${shared.input} w-auto`}
          value={tag}
          maxLength={40}
          placeholder={intl.formatMessage(messages.tagAll)}
          aria-label={intl.formatMessage(messages.tagAll)}
          onChange={(event) => onTag(event.target.value)}
        />
      </div>
    </>
  );
};

export const ImportCheckStep = TypedMemo(ImportCheckStepComponent);
